import React from 'react';
import SiteLayout, { PALETTE } from '../site/SiteLayout';
import SEO from '../../components/SEO';

const SITE_URL = 'https://keystone.cloudvoro.com';

const SECTION_TITLE = {
  fontFamily: "'Playfair Display', Georgia, serif",
  fontSize: 'clamp(22px, 2.6vw, 30px)',
  fontWeight: 700,
  color: PALETTE.navy,
  margin: '40px 0 14px',
  letterSpacing: '-0.015em',
};

const BODY = { fontSize: 15.5, lineHeight: 1.7, color: PALETTE.slate, margin: '0 0 14px' };

const Subhead = ({ children }) => (
  <h3 style={{
    fontSize: 13,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    fontWeight: 700,
    color: PALETTE.magenta,
    margin: '22px 0 6px',
  }}>{children}</h3>
);

const Row = ({ k, v }) => (
  <div style={{
    display: 'grid',
    gridTemplateColumns: '180px 1fr',
    gap: 16,
    padding: '10px 0',
    borderBottom: `1px solid ${PALETTE.border}`,
    fontSize: 14,
  }}>
    <div style={{ color: PALETTE.muted, fontWeight: 700, letterSpacing: '0.04em', fontSize: 12, textTransform: 'uppercase' }}>{k}</div>
    <div style={{ color: PALETTE.ink, lineHeight: 1.6 }}>{v}</div>
  </div>
);

const Card = ({ title, items }) => (
  <div style={{
    margin: '18px 0 24px',
    padding: '20px 24px',
    background: PALETTE.bgSoft,
    borderLeft: `3px solid ${PALETTE.magenta}`,
  }}>
    <div style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 17, fontWeight: 700, color: PALETTE.navy, marginBottom: 8 }}>{title}</div>
    {items.map((it) => <Row key={it.k} {...it} />)}
  </div>
);

export default function Permissions() {
  return (
    <SiteLayout>
      <SEO
        title="Content & Photography Permissions, Keystone by CloudVoro"
        description="Named, dated permissions log for every customer photograph, quote, brand reference and third-party trademark used on the Keystone marketing site. Updated when permissions change."
        path="/legal/permissions"
        keywords="Keystone permissions, CloudVoro content licence, Cashel Blue photo licence, customer quote permission food software, trademark attribution food software"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Content & Photography Permissions',
          url: `${SITE_URL}/legal/permissions`,
          description:
            'Dated permissions log for customer photography, quotes, brand references and trademark usage on the Keystone marketing site.',
        }}
      />

      <section style={{ background: PALETTE.cream, padding: '80px 32px 24px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }} data-testid="permissions-log">
          <div style={{
            fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta, marginBottom: 18,
          }}>Permissions log</div>
          <h1 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(36px, 5vw, 56px)',
            fontWeight: 700,
            color: PALETTE.navy,
            margin: 0,
            letterSpacing: '-0.025em',
            lineHeight: 1.05,
          }}>
            Whose name is on this site<br/>
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>and why.</em>
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.6, color: PALETTE.slate, marginTop: 22, maxWidth: 720 }}>
            Every customer photograph, named quote, brand reference and third-party trademark used on this site has a permission entry below. If you spot something missing or out of date, write to{' '}
            <a href="mailto:legal@cloudvoro.com" style={{ color: PALETTE.magenta, fontWeight: 600 }}>legal@cloudvoro.com</a>.
          </p>
          <div style={{ fontSize: 12, color: PALETTE.muted, marginTop: 18, letterSpacing: '0.04em' }}>
            Last reviewed: May 2026 · Reviewed quarterly
          </div>
        </div>
      </section>

      <section style={{ background: PALETTE.paper, padding: '32px 32px 96px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>

          <h2 style={SECTION_TITLE}>1. Founding customer, Cashel Farmhouse Cheesemakers</h2>
          <p style={BODY}>
            <strong style={{ color: PALETTE.navy }}>J&L Grubb Ltd</strong>, trading as <strong style={{ color: PALETTE.navy }}>Cashel Farmhouse Cheesemakers</strong>, Beechmount Farm, Fethard, Co. Tipperary, Ireland. Cashel Blue<sup>®</sup> and Crozier Blue<sup>®</sup> are registered trademarks of J&L Grubb Ltd.
          </p>
          <Card title="Brand references, Cashel Blue® / Crozier Blue®" items={[
            { k: 'Granted by', v: 'Louis Clifton Brown, QA & Operations Manager, J&L Grubb Ltd' },
            { k: 'Approved on', v: 'February 2026 (written confirmation on file)' },
            { k: 'Scope', v: 'Use of the Cashel Blue® and Crozier Blue® names, the Cashel Farmhouse Cheesemakers wordmark, and the Cashel Blue logo image (customers/cashel-blue-logo.webp) as a "Founding customer" reference across the Keystone marketing site, the brochure PDF, the Cashel Blue case study HTML page, and the Cashel Blue case study PDF.' },
            { k: 'Restrictions', v: 'Used with permission. Does not imply ownership or endorsement of Keystone by J&L Grubb Ltd beyond the factual scope described.' },
            { k: 'Revocation', v: 'Permission may be withdrawn in writing by an authorised representative of J&L Grubb Ltd. Removal across the site is committed within 14 calendar days of receipt.' },
          ]} />
          <Card title="Photography, Cashel Blue dairy floor & team" items={[
            { k: 'Granted by', v: 'Louis Clifton Brown, QA & Operations Manager, J&L Grubb Ltd' },
            { k: 'Approved on', v: 'February 2026' },
            { k: 'Files', v: '/customers/cashel-blue-team.png, /customers/cashel-blue-logo.webp, and the Playwright-captured screenshots of the Cashel Blue tenant of Keystone shown across the marketing site and brochure.' },
            { k: 'Scope', v: 'Marketing-site display, brochure PDF, case study HTML, OG preview images, social-media reposts of the case study, and procurement-facing decks.' },
            { k: 'Subjects', v: 'Where individuals are identifiable in any image, separate written consent has been obtained from those individuals.' },
          ]} />
          <Card title="Named quotes, Louis Clifton Brown, Sarah Furno, Sergio Furno" items={[
            { k: 'Status', v: 'No personal quotes attributed to any of these individuals appear on the site or in any marketing PDF. Placeholder pull-quotes previously drafted for design layout have all been removed pending on-record collection.' },
            { k: 'Next step', v: 'Long-form on-record testimonials are being gathered directly from each subject. Nothing will be published until we have their signed, verbatim words in writing.' },
            { k: 'In the meantime', v: 'A live reference call with Cashel Blue&rsquo;s QA &amp; Operations team can be arranged after a discovery call.' },
          ]} />

          <h2 style={SECTION_TITLE}>2. Retailer name references</h2>
          <p style={BODY}>
            Retailer names, <strong style={{ color: PALETTE.navy }}>Tesco, Musgrave (SuperValu / Centra), Lidl, ALDI, Dunnes Stores, M&S, SPAR, Dean & DeLuca</strong>, appear on this site in two contexts:
          </p>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li><strong style={{ color: PALETTE.navy }}>Dispatch-format examples</strong>, descriptive use to explain that Keystone produces dispatch notes formatted to match each retailer's public template. Names are used in good faith based on publicly available format specifications.</li>
            <li><strong style={{ color: PALETTE.navy }}>Demonstration data</strong>, fictional sales and dispatch records in the public sandbox use these retailer names as plausible counterparties. The transactions, batch numbers, quantities and dates shown are entirely fabricated.</li>
          </ul>
          <p style={BODY}>
            No retailer named on this site has endorsed, certified, partnered with, or commercially licensed Keystone. If you represent any of these retailers and would prefer alternative phrasing, please email{' '}
            <a href="mailto:legal@cloudvoro.com" style={{ color: PALETTE.magenta, fontWeight: 600 }}>legal@cloudvoro.com</a>, we respond within 5 business days.
          </p>

          <h2 style={SECTION_TITLE}>3. Third-party software trademarks</h2>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li><strong style={{ color: PALETTE.navy }}>AccountsIQ</strong>, trademark of AccountsIQ Technology Ltd. Referenced in good faith as an integration target; we are not a reseller or partner.</li>
            <li><strong style={{ color: PALETTE.navy }}>Sage, Xero, QuickBooks, SAP, Microsoft Dynamics, Microsoft Dynamics AX</strong>, referenced descriptively for integration and comparison context only. Each trademark belongs to its respective owner.</li>
            <li><strong style={{ color: PALETTE.navy }}>MRPeasy, Wherefour, Datatrack, Sage X3 Food & Beverage</strong>, competitor names referenced in good-faith comparison. Pricing and feature claims are based on each vendor's public materials as of May 2026 and may change.</li>
            <li><strong style={{ color: PALETTE.navy }}>BRCGS</strong>, BRCGS and the BRCGS Global Standard are property of LGC Group. Referenced descriptively.</li>
            <li><strong style={{ color: PALETTE.navy }}>FSAI, SFPA, HACCP</strong>, agency names and the HACCP framework (Codex Alimentarius) are referenced descriptively in their regulatory or scheme context.</li>
            <li><strong style={{ color: PALETTE.navy }}>Bord Bia, British Cheese Awards</strong>, referenced solely in connection with Cashel Blue's public award history.</li>
          </ul>

          <h2 style={SECTION_TITLE}>4. Founder photograph & biography</h2>
          <Card title="Founder image (/team/asif.jpg)" items={[
            { k: 'Subject', v: 'Asif Khan, Founder, CloudVoro' },
            { k: 'Permission', v: 'Self-licensed for unrestricted use on the Keystone marketing site, brochure and procurement materials.' },
          ]} />

          <h2 style={SECTION_TITLE}>5. Stock photography</h2>
          <p style={BODY}>
            Non-customer photographs (Irish dairy herd landscapes, generic factory-floor stock) are sourced from <strong style={{ color: PALETTE.navy }}>Unsplash</strong> under the Unsplash licence, which permits commercial use without attribution. Where attribution is requested by the photographer, it is included in the page <code>{'<noscript>'}</code> body and the <code>alt</code> text.
          </p>

          <h2 style={SECTION_TITLE}>6. Affiliation logos</h2>
          <ul style={{ ...BODY, paddingLeft: 22 }}>
            <li><strong style={{ color: PALETTE.navy }}>BCS, British Computer Society</strong> · membership badge used under BCS member-use guidance.</li>
            <li><strong style={{ color: PALETTE.navy }}>ICS, Irish Computer Society</strong> · membership badge used under ICS member-use guidance.</li>
            <li><strong style={{ color: PALETTE.navy }}>ADPO, Association of Data Protection Officers Ireland</strong> · membership badge used under ADPO member-use guidance. (Membership is held for professional development; CloudVoro has not appointed an Article 37 DPO, see <a href="/site/security" style={{ color: PALETTE.magenta, fontWeight: 600 }}>/site/security</a>.)</li>
            <li><strong style={{ color: PALETTE.navy }}>IMCA, Institute of Management Consultants & Advisors</strong> · membership badge used under IMCA member-use guidance.</li>
            <li><strong style={{ color: PALETTE.navy }}>HISI, Healthcare Informatics Society of Ireland</strong> · membership badge used under HISI member-use guidance.</li>
          </ul>

          <h2 style={SECTION_TITLE}>7. Reporting a permissions issue</h2>
          <p style={BODY}>
            If you believe content on this site has been published without your permission, write to{' '}
            <a href="mailto:legal@cloudvoro.com" style={{ color: PALETTE.magenta, fontWeight: 600 }}>legal@cloudvoro.com</a>{' '}
            with a description of the content and your relationship to it. We acknowledge within 2 business days and aim to remove or correct unauthorised material within 14 calendar days of verification.
          </p>

        </div>
      </section>
    </SiteLayout>
  );
}
